/**
 * PackageBuilder — turns a resolved disclosure list into a signable package.
 *
 * Ported from legacy SPFx `services/PackageBuilder.ts` + `DocumentGeneratorService`.
 * The legacy service called `MergeEngine` to produce the full HTML per doc; v3
 * delegates that to the server (`/templates/compile` → Playwright) so the
 * client only ships the plan + merge data, not bytes.
 *
 * Two paths:
 *   1. Live compile — for each resolved disclosure, if the shared catalog has
 *      `versions[0].htmlBody`, we post it to `/templates/compile` and store
 *      the `fieldCount` + `pageCount` for UI preview.  Compiled bytes
 *      (base64 PDF) are NOT returned from here — PackageBuilder only sizes
 *      the package and surfaces preview metadata.  SignStep.start() hands the
 *      full `HtmlDocuments[]` array to `/signing/start` which is what actually
 *      triggers the server pipeline (Playwright render → merge → field extraction).
 *
 *   2. Demo preview — when running outside a seeded SQL environment (503s on
 *      `/v3/config/disclosures`), we still produce a plausible list so the
 *      SignStep UI can show "this is what would be signed".  Each row is
 *      tagged `isDemoPreview: true` and PackageBuilder returns a `mode` of
 *      `'demo-preview'` so the UI banner reads
 *      "Demo preview — live compile lands Wave 5".
 *
 * The engine is async because `/templates/compile` is a real network call; the
 * caller can pass `{ mode: 'preview-only' }` to short-circuit for the UI-only
 * first render (before the officer clicks "Prepare package").
 */
import type {
  DisclosureTemplate,
  DisclosureTemplateVersion,
  SigningSignerInput,
} from '@retail-ops/shared-types';
import type { ApiClient } from '@retail-ops/api-client';
import type { ResolvedDisclosure } from './DisclosureEngine';
import type { RaoPerson } from '../types';

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export type PackageMode = 'live-compile' | 'demo-preview' | 'preview-only';

/** One built package document — what the preview pane renders. */
export interface PackageDocument {
  /** Stable instance id — PreparePackage/Cache-PDFs keys off this. */
  instanceId: string;
  /** Matches ResolvedDisclosure.templateCode. */
  templateCode: string;
  /** Display title. */
  title: string;
  /** From the resolved row. */
  required: boolean;
  /** 'required' | 'view-only' — maps to SignNow invite_actions. */
  signatureRequirement: 'required' | 'view-only';
  /** Roles that will sign this document (Primary, Joint1, ...). */
  signerRoles: ReadonlyArray<string>;
  /** Known / estimated page count. 0 if unknown. */
  pageCount: number;
  /** Known / estimated field count (signature + date + initials). 0 if unknown. */
  fieldCount: number;
  /** HTML body the server will render (only populated in live-compile mode). */
  html?: string;
  /** True if this row came from the demo-preview fixture. */
  isDemoPreview: boolean;
  /** Compile error surfaced per-doc so the UI can keep rendering others. */
  compileError?: string;
}

export interface BuiltPackage {
  mode: PackageMode;
  documents: ReadonlyArray<PackageDocument>;
  signers: ReadonlyArray<SigningSignerInput>;
  /** Aggregate stats for the preview banner. */
  totals: { docs: number; pages: number; fields: number; signers: number };
  /** When any compile step failed — the UI should surface this. */
  errors: ReadonlyArray<{ templateCode: string; error: string }>;
}

export interface BuildPackageInput {
  resolved: ReadonlyArray<ResolvedDisclosure>;
  disclosureTemplates: ReadonlyArray<DisclosureTemplate>;
  /**
   * Versions array (sibling of `disclosureTemplates` from the v3 disclosures
   * envelope). PackageBuilder picks the active body from
   * `disclosureVersions.find(v => v.templateCode === code && v.versionNumber === '1.0')`.
   * Optional for callers that haven't been updated yet — when omitted we fall
   * back to the parent template's inline `disclosureBody` field.
   */
  disclosureVersions?: ReadonlyArray<DisclosureTemplateVersion>;
  people: ReadonlyArray<RaoPerson>;
  /** Cart product lines. Ownership (primaryOwnerId / jointOwnerIds) decides who signs —
   *  a person in the cart but on no product is NOT a signer. Bill 2026-07-16. */
  products?: ReadonlyArray<Record<string, unknown>>;
  /** FSR identity for the optional BankRep audit stamp (not a signer). */
  fsrEmail?: string;
  fsrName?: string;
  /** Force a specific mode.  Default: auto-detect (live-compile when server
   *  is reachable AND template has htmlBody; demo-preview otherwise). */
  mode?: PackageMode;
  /** Optional API client for `/templates/compile`.  Required for live-compile. */
  client?: ApiClient;
  /** AbortSignal for the compile fan-out. */
  signal?: AbortSignal;
}

// ---------------------------------------------------------------------------
// UUID (matches legacy PackageBuilder.generateUUID)
// ---------------------------------------------------------------------------

function generateInstanceId(): string {
  const g = (typeof crypto !== 'undefined') ? crypto : undefined;
  if (g && typeof g.randomUUID === 'function') return g.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ---------------------------------------------------------------------------
// Signer assembly (SigningSignerInput shape from shared-types)
// ---------------------------------------------------------------------------

/**
 * A minor is never a signer (Bill 2026-07-27/28). Deliberately SEPARATE from the
 * ownership gate: ownership fixed an earlier incident where a minor who owned
 * NOTHING was pushed in front of the kiosk, but a minor who legitimately OWNS the
 * account (Minor My Student Checking) passes ownership and would still be asked to
 * sign. Ownership is a stake; capacity is permission to sign for it.
 *
 * MUST stay in lockstep with the server's DisclosureRenderOrchestrator.IsMinorPerson
 * — the signer INDEX is a position into this list, so a disagreement pushes a step
 * the server cannot place.
 */
export function isMinorPerson(p: { customerType?: string | null; dob?: string | null }): boolean {
  if ((p.customerType ?? '').trim().toLowerCase() === 'minor') return true;
  const raw = (p.dob ?? '').trim();
  if (!raw) return false;
  const dob = new Date(raw);
  if (Number.isNaN(dob.getTime())) return false;
  const today = new Date();
  let age = today.getUTCFullYear() - dob.getUTCFullYear();
  const m = today.getUTCMonth() - dob.getUTCMonth();
  if (m < 0 || (m === 0 && today.getUTCDate() < dob.getUTCDate())) age--;
  return age >= 0 && age < 18;
}

/**
 * Build the SignNow `Signers[]` array from cart.people.  Matches legacy
 * `SignNowClientService.start` payload shape.
 *
 * - Primary + Joints only — beneficiaries / POD are NOT signers.
 * - SigningOrder mirrors array index (Primary=1, Joint1=2, ...).
 * - Method defaults to 'email'; SignStep UI overrides to 'sms' / 'in-person'.
 * - BankRep is NOT included — FSR is an audit-trail identity, not a SignNow
 *   signer (see signnow-architecture.md §"BankRep Is NOT a SignNow Signer").
 *
 * ── AcroForm ↔ SignNow field-name contract (S4.1, 2026-06-11) ──────────────
 * The `Role` strings produced here ('Primary', 'Joint1', 'Joint2', …) are the
 * SignNow `role_name` binding tokens.  They MUST match, case-insensitively:
 *   1. the `{ROLE}_SIGNATURE` prefix of the `<pcb-field type="signature">`
 *      names in the disclosure templates (e.g. PRIMARY_SIGNATURE), and
 *   2. the server's DisclosureRenderOrchestrator.ExtractSignerRoles output
 *      (same Primary/JointN derivation from cart.people — keep in lockstep).
 * Allowed characters: letters/digits/apostrophe/period only (SignNow text-tag
 * `o:"…"` restriction) — never emit hyphens (no 'Joint-1').
 * /signing/start validates the binding server-side before dispatch and
 * returns HTTP 422 with a plain-language Error on mismatch — surface that
 * Error text to the FSR verbatim.  Full contract: apps/PCBJxChangeApi/
 * Services/SignNowService.cs (UploadDocumentWithFieldExtractAsync header) +
 * docs/operations/signing-field-name-contract.md.
 */
/**
 * The set of person-ids that actually OWN something in this cart.
 *
 * Bill 2026-07-16, after smoke-testing with Peter: "Roles should never be cart based.
 * That is the major issue we have been fighting."
 *
 * A person is a primary/joint OF A PRODUCT — never of a cart. The cart is a workspace,
 * not a relationship. Bill's own test proves a cart-level role cannot even represent the
 * truth: Ultimate Checking was A-primary/B-joint while Savings was B-primary/A-joint.
 * One field, two contradictory answers.
 *
 * Reading `person.role` is what put Sample Beneficiary — a minor, added to the cart and
 * assigned to NO product — in front of the kiosk to e-consent and sign both signature
 * cards AND the overdraft form. That is a compliance failure, not a display bug.
 *
 * So ownership is the authority: a signer is someone named as primaryOwnerId or in
 * jointOwnerIds on at least one product line.
 */
function productOwnerIds(products: ReadonlyArray<Record<string, unknown>>): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const p of products) {
    const primary = p['primaryOwnerId'];
    if (typeof primary === 'string' && primary) ids.add(primary);
    const joints = p['jointOwnerIds'];
    if (Array.isArray(joints)) {
      for (const j of joints) if (typeof j === 'string' && j) ids.add(j);
    }
  }
  return ids;
}

export function buildSigners(
  people: ReadonlyArray<RaoPerson>,
  defaultMethod: 'email' | 'sms' | 'in-person' = 'email',
  products: ReadonlyArray<Record<string, unknown>> = [],
): ReadonlyArray<SigningSignerInput> {
  const signers: SigningSignerInput[] = [];
  let order = 1;
  let jointSeq = 0;

  // Who owns a product? When products are supplied this is the ONLY gate on who signs.
  // Passing none keeps the legacy cart-role behaviour so an un-migrated caller can't
  // silently produce an EMPTY signer list (nobody signs = a worse failure than the bug
  // being fixed). Every caller should pass them; see productOwnerIds above.
  const owners = productOwnerIds(products);
  const gateOnOwnership = owners.size > 0;

  for (const person of people) {
    // A MINOR IS NEVER A SIGNER — checked before ownership because it is
    // unconditional: a minor owner still may not sign for the account.
    if (isMinorPerson(person as { customerType?: string | null; dob?: string | null })) continue;

    if (gateOnOwnership) {
      // Ownership decides IF they sign.
      if (!person.id || !owners.has(person.id)) continue;
    } else if (person.role !== 'primary' && person.role !== 'joint') {
      continue;
    }

    // …and the cart role still orders the {ROLE}_SIGNATURE binding (Primary first). That
    // is a KNOWN half-measure: with per-product ownership a person can be primary on one
    // account and joint on another, which a single cart-level rank cannot express. The
    // real model is per-product signer roles; this stops the wrong PEOPLE signing today
    // without pretending the role model is finished. See
    // feedback_roles_are_never_cart_based_always_account_party_20260716.
    // Only the FIRST primary is 'Primary'; a second cart-level primary signs as JointN. Lockstep
    // with the server's ExtractSignerAssignments (Bill 2026-10-14 smoke cart: Amy and Scott were
    // both cart role 'primary'; the server asked Primary + Joint1 to sign the POD form, the client
    // labelled both 'Primary', and the configure dialog showed Scott as documents only).
    const role = person.role === 'primary' && !signers.some((sg) => sg.Role === 'Primary')
      ? 'Primary'
      : (() => { jointSeq += 1; return `Joint${jointSeq}`; })();

    const name = person.fullName
      || `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim()
      || 'Unknown Signer';

    const email = person.email ?? person.email2 ?? '';

    const signer: SigningSignerInput = {
      Name: name,
      ...(person.firstName ? { FirstName: person.firstName } : {}),
      ...(person.lastName ? { LastName: person.lastName } : {}),
      Email: email,
      ...(person.phoneCell ? { Phone: person.phoneCell } : {}),
      Role: role,
      Method: defaultMethod,
      InPerson: defaultMethod === 'in-person',
      SigningOrder: order,
    };
    signers.push(signer);
    order += 1;
  }

  // Never hand back an EMPTY roster because of the minor rule — nobody-signs is a
  // worse failure than over-asking (the same reasoning this file already applies to
  // un-migrated callers). A minor-owned account whose adult is a custodian rather
  // than a named product owner falls back to the non-minor cart people.
  if (signers.length === 0) {
    let fbOrder = 1;
    let fbJoint = 0;
    for (const person of people) {
      if (isMinorPerson(person as { customerType?: string | null; dob?: string | null })) continue;
      const r = (person.role ?? '').toLowerCase();
      if (r !== 'primary' && r !== 'joint' && r !== 'custodian' && r !== 'guardian') continue;
      const role = r === 'primary' && !signers.some((sg) => sg.Role === 'Primary') ? 'Primary' : (() => { fbJoint += 1; return `Joint${fbJoint}`; })();
      const name = person.fullName
        || `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim()
        || 'Unknown Signer';
      signers.push({
        Name: name,
        ...(person.firstName ? { FirstName: person.firstName } : {}),
        ...(person.lastName ? { LastName: person.lastName } : {}),
        Email: person.email ?? person.email2 ?? '',
        ...(person.phoneCell ? { Phone: person.phoneCell } : {}),
        Role: role,
        Method: defaultMethod,
        InPerson: defaultMethod === 'in-person',
        SigningOrder: fbOrder,
      });
      fbOrder += 1;
    }
  }

  return signers;
}

// ---------------------------------------------------------------------------
// Demo preview — fixture used when SQL isn't seeded yet
// ---------------------------------------------------------------------------

/**
 * Produce a plausible preview list from the resolved disclosures without
 * touching `/templates/compile`.  The fixture uses legacy page-count averages
 * observed from the PCB template corpus (see `Docs/analysis/*.md` — 276 PDFs).
 */
function buildDemoPreview(
  resolved: ReadonlyArray<ResolvedDisclosure>,
  signers: ReadonlyArray<SigningSignerInput>,
): BuiltPackage {
  const documents: PackageDocument[] = resolved.map((r) => ({
    instanceId: generateInstanceId(),
    templateCode: r.templateCode,
    title: r.title,
    required: r.required,
    signatureRequirement: r.requiresSignature ? 'required' : 'view-only',
    signerRoles: r.signerRoles,
    pageCount: estimatedPageCount(r.templateCode),
    fieldCount: r.requiresSignature ? Math.max(1, r.signerRoles.length * 2) : 0,
    isDemoPreview: true,
  }));

  return {
    mode: 'demo-preview',
    documents,
    signers,
    totals: aggregate(documents, signers),
    errors: [],
  };
}

/** Plausible page counts per legacy doc category. */
function estimatedPageCount(templateCode: string): number {
  // Quick-n-dirty bucket map from observed PCB templates.
  const MAP: Record<string, number> = {
    DEP_AGREE:        22,
    PRIVACY:          2,
    SIG_CARD:         1,
    REGCC_DISC:       3,
    REG_E_OPTIN:      2,
    ODP_FORM:         2,
    RATE_SCHED:       2,
    EFT_DISC:         2,
    BO_CERT:          3,
    IRA_CONTRIB_ELIG: 4,
    CD_CONFIRM:       1,
  };
  if (MAP[templateCode] !== undefined) return MAP[templateCode];
  const prefix = Object.keys(MAP).find((k) => templateCode.startsWith(k));
  return prefix !== undefined ? MAP[prefix] : 2;
}

function aggregate(
  documents: ReadonlyArray<PackageDocument>,
  signers: ReadonlyArray<SigningSignerInput>,
): { docs: number; pages: number; fields: number; signers: number } {
  const pages = documents.reduce((s, d) => s + d.pageCount, 0);
  const fields = documents.reduce((s, d) => s + d.fieldCount, 0);
  return { docs: documents.length, pages, fields, signers: signers.length };
}

// ---------------------------------------------------------------------------
// Live compile — fan out to /templates/compile per template
// ---------------------------------------------------------------------------

/**
 * For each resolved template that has HTML in the catalog, call
 * `/templates/compile` to get the server-side field count (authoritative —
 * AcroForm-extracted) and compiled PDF size.  We DON'T keep the base64 bytes
 * in memory — too expensive for the UI.  The SignStep start flow re-posts
 * the HTML via `/signing/prepare-package` which is the thing that actually
 * renders + merges + uploads to SignNow.
 */
async function compileLive(
  resolved: ReadonlyArray<ResolvedDisclosure>,
  templateCatalog: ReadonlyArray<DisclosureTemplate>,
  templateVersions: ReadonlyArray<DisclosureTemplateVersion> | undefined,
  signers: ReadonlyArray<SigningSignerInput>,
  client: ApiClient,
  signal?: AbortSignal,
): Promise<BuiltPackage> {
  const documents: PackageDocument[] = [];
  const errors: Array<{ templateCode: string; error: string }> = [];

  await Promise.all(resolved.map(async (row) => {
    const tpl = templateCatalog.find((t) => t.code === row.templateCode);
    // Body resolution order: explicit version body > inline parent body.
    const version = templateVersions?.find(
      (v) => v.templateCode === row.templateCode && v.versionNumber === '1.0',
    );
    const html = version?.contentHtml ?? tpl?.disclosureBody ?? undefined;

    const baseDoc: PackageDocument = {
      instanceId: generateInstanceId(),
      templateCode: row.templateCode,
      title: row.title,
      required: row.required,
      signatureRequirement: row.requiresSignature ? 'required' : 'view-only',
      signerRoles: row.signerRoles,
      pageCount: estimatedPageCount(row.templateCode),
      fieldCount: 0,
      ...(html ? { html } : {}),
      isDemoPreview: !html, // no html yet → preview only
    };

    if (!html) {
      // No published version → still include, but mark as demo-preview so the
      // UI can show "awaiting admin authoring" without failing the package.
      documents.push(baseDoc);
      return;
    }

    try {
      const res = await client.templates.compile(
        { TemplateCode: row.templateCode, HtmlContent: html },
        signal ? { signal } : undefined,
      );
      if (res.success) {
        documents.push({
          ...baseDoc,
          fieldCount: res.fieldCount ?? baseDoc.fieldCount,
          isDemoPreview: false,
        });
      } else {
        documents.push({
          ...baseDoc,
          compileError: res.error ?? 'Compile returned success=false',
        });
        errors.push({
          templateCode: row.templateCode,
          error: res.error ?? 'Compile returned success=false',
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown compile error';
      documents.push({ ...baseDoc, compileError: msg });
      errors.push({ templateCode: row.templateCode, error: msg });
    }
  }));

  // Preserve resolved-list ordering (Promise.all preserves indexes, but sort
  // defensively by the resolved row order anyway).
  const indexOf = new Map<string, number>();
  resolved.forEach((r, i) => indexOf.set(r.templateCode, i));
  documents.sort((a, b) =>
    (indexOf.get(a.templateCode) ?? 99) - (indexOf.get(b.templateCode) ?? 99),
  );

  return {
    mode: 'live-compile',
    documents,
    signers,
    totals: aggregate(documents, signers),
    errors,
  };
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

/**
 * Build the package.  Safe to call on every SignStep render.
 *
 * Mode resolution:
 *   - explicit opts.mode wins
 *   - else: if NO catalog template has an htmlBody OR no client passed →
 *     `demo-preview`
 *   - else `live-compile`
 */
export async function buildPackage(input: BuildPackageInput): Promise<BuiltPackage> {
  const signers = buildSigners(input.people, undefined, input.products ?? []);
  const { resolved, disclosureTemplates, disclosureVersions, client, signal } = input;

  // Empty resolved list → empty package (SignStep will show an info banner).
  if (resolved.length === 0) {
    return {
      mode: input.mode ?? 'demo-preview',
      documents: [],
      signers,
      totals: { docs: 0, pages: 0, fields: 0, signers: signers.length },
      errors: [],
    };
  }

  // A template "has body" if either (a) the version row carries it, or
  // (b) the inline `disclosureBody` is non-placeholder.
  const anyHasHtml =
    (disclosureVersions ?? []).some((v) => (v.contentHtml?.length ?? 0) > 0) ||
    disclosureTemplates.some((t) => (t.disclosureBody?.length ?? 0) > 0 && !t.disclosureBodyIsPlaceholder);

  let mode: PackageMode = input.mode ?? (client && anyHasHtml ? 'live-compile' : 'demo-preview');

  if (mode === 'preview-only') {
    // Preview-only — no network call, just use the demo fixture.
    return buildDemoPreview(resolved, signers);
  }

  if (mode === 'live-compile' && client) {
    try {
      return await compileLive(resolved, disclosureTemplates, disclosureVersions, signers, client, signal);
    } catch {
      // If the fan-out itself failed (offline, 503 everywhere), degrade to
      // preview so the officer still sees what would be sent.
      mode = 'demo-preview';
    }
  }

  return buildDemoPreview(resolved, signers);
}
